import type { MapViewState } from '@deck.gl/core'
import { filter, includes, uniq } from 'lodash-es'
import { INITIAL_VIEW_STATE } from 'src/pages/vmap/map/Map'
import { type BulkListVehicle } from 'src/utils/scooterBulk/types'
import { getCenterZoomForBounds, getElementDimensions } from 'src/utils/map/viewportUtils'

interface Bounds {
    minLat: number
    minLon: number
    maxLat: number
    maxLon: number
}

export const getOptimisedViewState = (
    mapWrapper: HTMLDivElement | undefined,
    vehicles: BulkListVehicle[],
): MapViewState => {
    const locations = vehicles.map(vehicle => vehicle.location)
    const bounds = getBounds(locations)
    const dimensions = getElementDimensions(mapWrapper)
    // When only one vehicle displayed, use the vehicle location as center and set zoom level 20.
    const centerZoom =
        locations.length === 1
            ? { longitude: locations[0][1], latitude: locations[0][0], zoom: 20 }
            : getCenterZoomForBounds(
                  { minLng: bounds.minLon, minLat: bounds.minLat, maxLng: bounds.maxLon, maxLat: bounds.maxLat },
                  dimensions,
              )

    const optimisedViewState: MapViewState = {
        ...INITIAL_VIEW_STATE,
        longitude: centerZoom.longitude,
        latitude: centerZoom.latitude,
        zoom: centerZoom.zoom,
    }

    return optimisedViewState
}

const getBounds = (locations: [number, number][]): Bounds => {
    if (locations.length > 0) {
        return locations.reduce(
            (prev, curr) => {
                const minLon = curr[1] < prev.minLon ? curr[1] : prev.minLon
                const minLat = curr[0] < prev.minLat ? curr[0] : prev.minLat
                const maxLon = curr[1] > prev.maxLon ? curr[1] : prev.maxLon
                const maxLat = curr[0] > prev.maxLat ? curr[0] : prev.maxLat

                const bounds: Bounds = {
                    minLat,
                    minLon,
                    maxLat,
                    maxLon,
                }

                return bounds
            },
            {
                minLon: 180,
                minLat: 90,
                maxLon: -180,
                maxLat: -90,
            } as Bounds,
        )
    }

    const bounds: Bounds = {
        minLat: 0,
        minLon: 0,
        maxLat: 0,
        maxLon: 0,
    }

    return bounds
}

export const handleDuplicatedSearch = (searchedText: string): { uniqText: string[]; duplicatedText: string[] } => {
    const text = searchedText.split(',')
    const duplicatedText = filter(text, (value, index, iteratee) => includes(iteratee, value, index + 1))
    const uniqDuplicatedText = uniq(duplicatedText)
    const uniqText = uniq(text)

    return { uniqText: uniqText, duplicatedText: uniqDuplicatedText }
}
